import { useState, useCallback } from 'react';
import { Copy, Check, Loader2, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import { api, ContactShare, ContactDetail } from '@/lib/api';
import { formatContactName } from './utils';

export function ShareDialog({
  open,
  onOpenChange,
  contact,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: ContactDetail | null;
}) {
  const [share, setShare] = useState<ContactShare | null>(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateLink = useCallback(async () => {
    if (!contact) return;
    setGenerating(true);
    try {
      const result = await api.shareContact(contact.id);
      setShare(result);
    } catch (err) {
      toast({
        title: 'Failed to generate share link',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  }, [contact]);

  const shareUrl = share
    ? `${window.location.origin}/v1/arches/contact-book/shared/${share.token}`
    : '';

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ description: 'Link copied to clipboard.' });
    } catch {
      toast({ title: 'Failed to copy', variant: 'destructive' });
    }
  }, [shareUrl]);

  const handleClose = useCallback(() => {
    setShare(null);
    setCopied(false);
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share Contact</DialogTitle>
          <DialogDescription>
            {contact
              ? `Share ${formatContactName(contact)} via a temporary link.`
              : 'Generate a shareable link for this contact.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {!share ? (
            <div className="flex flex-col items-center gap-4 py-4">
              <Link2 className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground text-center">
                Generate a link that anyone can use to view this contact's basic info and download
                their vCard. The link expires after 7 days.
              </p>
              <Button onClick={generateLink} disabled={generating}>
                {generating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Generate Link
              </Button>
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <Input value={shareUrl} readOnly className="text-sm" />
                <Button variant="outline" size="icon" onClick={handleCopy}>
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Expires:{' '}
                {new Date(share.expiresAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </p>
              <Button variant="outline" size="sm" onClick={generateLink} disabled={generating}>
                {generating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Generate New Link
              </Button>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
